import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, InputGroup, Modal, Spinner } from 'react-bootstrap';
import api from '../api/axiosConfig';
import { UserDto } from '../types';

interface Props {
    show: boolean;
    user?: UserDto | null;
    onClose: () => void;
    onSaved: () => void;
}

const ResetPasswordModal: React.FC<Props> = ({ show, user, onClose, onSaved }) => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [needPasswordChange, setNeedPasswordChange] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!show) return;
        setPassword('');
        setConfirmPassword('');
        setShowPassword(false);
        setNeedPasswordChange(true);
        setError(null);
    }, [show, user]);

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!user) return;
        if (password !== confirmPassword) {
            setError('Пароли не совпадают');
            return;
        }

        setLoading(true);
        setError(null);
        try {
            await api.patch(`/users/${user.id}/password-reset`, { password, needPasswordChange });
            onSaved();
        } catch (e: any) {
            const body = e.response?.data;
            setError(typeof body === 'string' ? body : body?.message || 'Не удалось изменить пароль');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={onClose} centered>
            <Form onSubmit={submit}>
                <Modal.Header closeButton>
                    <Modal.Title>Изменить пароль</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    <Form.Group className="mb-3">
                        <Form.Label>Новый пароль</Form.Label>
                        <InputGroup>
                            <Form.Control
                                required
                                minLength={6}
                                type={showPassword ? 'text' : 'password'}
                                autoComplete="new-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                            <Button
                                variant="outline-secondary"
                                type="button"
                                onClick={() => setShowPassword((value) => !value)}
                                aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                            >
                                <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} />
                            </Button>
                        </InputGroup>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Повторите пароль</Form.Label>
                        <InputGroup>
                            <Form.Control
                                required
                                minLength={6}
                                type={showPassword ? 'text' : 'password'}
                                autoComplete="new-password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                            />
                            <Button
                                variant="outline-secondary"
                                type="button"
                                onClick={() => setShowPassword((value) => !value)}
                                aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                            >
                                <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} />
                            </Button>
                        </InputGroup>
                    </Form.Group>
                    <Form.Check
                        className="mt-3"
                        type="switch"
                        label="Временный пароль: попросить пользователя сменить пароль при входе"
                        checked={needPasswordChange}
                        onChange={(e) => setNeedPasswordChange(e.target.checked)}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={onClose}>Отмена</Button>
                    <Button type="submit" disabled={loading || !user}>
                        {loading && <Spinner size="sm" className="me-2" />}
                        Сохранить пароль
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default ResetPasswordModal;
